<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <div class="bc-head">
          <div>
            <h3>Bank &amp; Cheques</h3>
            <p>Track cheques you issue and receive, and see whether the bank balance will cover them.</p>
          </div>
          <div v-if="canEdit" class="bc-head-actions">
            <button type="button" class="bc-btn-light" @click="openBalance()"><i class="fas fa-university"></i> Update balance</button>
            <button type="button" class="bc-btn-primary" @click="openChequeModal()"><i class="fas fa-plus"></i> Add cheque</button>
          </div>
        </div>

        <!-- KPIs -->
        <div class="bc-kpis">
          <button type="button" class="bc-kpi bc-kpi-balance" @click="setTab('balance')">
            <span><i class="fas fa-university"></i> Bank balance</span>
            <strong>{{ money(lastKnownBalance) }}</strong>
            <small v-if="latestBalanceDate" :class="{ stale: balanceIsStale }">
              {{ balanceIsStale ? "Last updated" : "As of" }} {{ formatDate(latestBalanceDate) }}
            </small>
            <small v-else class="stale">Not entered yet</small>
          </button>
          <button type="button" class="bc-kpi" @click="showRegister('PENDING', 'ISSUED')">
            <span><i class="fas fa-arrow-up out"></i> To pay</span>
            <strong>{{ money(pending.issued.amount) }}</strong>
            <small>{{ pending.issued.count }} pending cheque{{ pending.issued.count === 1 ? "" : "s" }}</small>
          </button>
          <button type="button" class="bc-kpi" @click="showRegister('PENDING', 'RECEIVED')">
            <span><i class="fas fa-arrow-down in"></i> To receive</span>
            <strong>{{ money(pending.received.amount) }}</strong>
            <small>{{ pending.received.count }} pending cheque{{ pending.received.count === 1 ? "" : "s" }}</small>
          </button>
          <button type="button" class="bc-kpi" :class="{ alert: overdue.length }" @click="setTab('upcoming')">
            <span><i class="fas fa-exclamation-circle"></i> Overdue</span>
            <strong>{{ overdue.length }}</strong>
            <small>{{ overdue.length ? "past clearance date — update status" : "none, all good" }}</small>
          </button>
          <div class="bc-kpi bc-kpi-projected" :class="{ negative: projectedEnd < 0 }">
            <span><i class="fas fa-chart-line"></i> After all pending clear</span>
            <strong>{{ money(projectedEnd) }}</strong>
            <small>balance + to receive − to pay</small>
          </div>
        </div>

        <div v-if="shortfall" class="bc-warning">
          <i class="fas fa-exclamation-triangle"></i>
          <span>
            On <strong>{{ formatDate(shortfall.date) }}</strong> the bank balance could fall to <strong>{{ money(shortfall.balance) }}</strong>
            when issued cheques clear. Deposit at least <strong>{{ money(-shortfall.balance) }}</strong> before then to avoid a bounce.
          </span>
        </div>

        <div class="bc-tabs" role="tablist">
          <button v-for="tab in tabs" :key="tab.key" type="button" role="tab" :class="{ on: activeTab === tab.key }" :aria-selected="activeTab === tab.key" @click="setTab(tab.key)">
            <i :class="tab.icon"></i> {{ tab.label }} <span v-if="tab.count">{{ tab.count }}</span>
          </button>
        </div>

        <div v-if="loading" class="bc-card bc-empty"><i class="fas fa-spinner fa-spin"></i> Loading...</div>

        <!-- ═════════ UPCOMING ═════════ -->
        <div v-else-if="activeTab === 'upcoming'" class="bc-card bc-timeline-card">
          <div v-if="!overdue.length && !timeline.length" class="bc-empty">
            <div class="bc-empty-icon"><i class="fas fa-calendar-check"></i></div>
            <strong>No pending cheques</strong>
            <span>Cheques you issue or receive will appear here by the date they are expected to clear.</span>
            <button v-if="canEdit" type="button" class="bc-btn-primary mt-2" @click="openChequeModal()"><i class="fas fa-plus"></i> Add cheque</button>
          </div>

          <div v-if="overdue.length" class="bc-day overdue">
            <div class="bc-day-head">
              <div>
                <strong><i class="fas fa-exclamation-circle"></i> Overdue</strong>
                <small>Expected to clear before today — mark them cleared or bounced</small>
              </div>
            </div>
            <cheque-line v-for="cheque in overdue" :key="cheque.chequeid" :cheque="cheque" :can-edit="canEdit" :format-date="formatDate" :money="money" @status="updateChequeStatus" />
          </div>

          <div v-for="day in timeline" :key="day.date" class="bc-day">
            <div class="bc-day-head">
              <div>
                <strong>{{ dayLabel(day.date) }}</strong>
                <small>{{ formatDate(day.date) }}</small>
              </div>
              <div class="bc-day-figures">
                <span v-if="day.outgoing" class="out">−{{ money(day.outgoing) }}</span>
                <span v-if="day.incoming" class="in">+{{ money(day.incoming) }}</span>
                <span class="bc-proj" :class="{ negative: day.balanceAfter < 0 }" title="Projected bank balance after this day">
                  Balance after: <strong>{{ money(day.balanceAfter) }}</strong>
                </span>
              </div>
            </div>
            <cheque-line v-for="cheque in day.cheques" :key="cheque.chequeid" :cheque="cheque" :can-edit="canEdit" :format-date="formatDate" :money="money" @status="updateChequeStatus" />
          </div>
        </div>

        <!-- ═════════ REGISTER ═════════ -->
        <div v-else-if="activeTab === 'register'" class="bc-card">
          <div class="bc-filters">
            <div class="bc-seg">
              <button v-for="tab in statusTabs" :key="tab.value" type="button" :class="{ on: filters.status === tab.value }" @click="filters.status = tab.value">
                {{ tab.label }} <span>{{ tab.count }}</span>
              </button>
            </div>
            <div class="bc-seg">
              <button type="button" :class="{ on: !filters.type }" @click="filters.type = ''">All</button>
              <button type="button" :class="{ on: filters.type === 'ISSUED' }" @click="filters.type = 'ISSUED'"><i class="fas fa-arrow-up out"></i> To pay</button>
              <button type="button" :class="{ on: filters.type === 'RECEIVED' }" @click="filters.type = 'RECEIVED'"><i class="fas fa-arrow-down in"></i> To receive</button>
            </div>
            <div class="bc-search">
              <i class="fas fa-search"></i>
              <input v-model.trim="filters.search" type="search" placeholder="Party or cheque no..." />
            </div>
          </div>

          <div v-if="!registerRows.length" class="bc-empty">
            <strong>No cheques match</strong>
            <span>Try another status or clear the search.</span>
          </div>
          <div v-else class="bc-register">
            <cheque-line v-for="cheque in registerRows" :key="cheque.chequeid" :cheque="cheque" :can-edit="canEdit" :format-date="formatDate" :money="money" show-dates @status="updateChequeStatus" />
          </div>
          <div v-if="registerRows.length" class="bc-register-total">
            {{ registerRows.length }} cheque{{ registerRows.length === 1 ? "" : "s" }} &middot;
            <span class="out">to pay {{ money(sumOf(registerRows, 'ISSUED')) }}</span> &middot;
            <span class="in">to receive {{ money(sumOf(registerRows, 'RECEIVED')) }}</span>
          </div>
        </div>

        <!-- ═════════ BALANCE ═════════ -->
        <div v-else class="bc-balance-layout">
          <div class="bc-card bc-balance-entry">
            <h5>{{ todayEntry ? "Today's closing balance" : "Enter today's closing balance" }}</h5>
            <p>Check your bank statement or app at the end of the day. This is used to warn you before cheques bounce.</p>
            <div class="bc-amount" :class="{ disabled: !canEdit }">
              <span>₹</span>
              <input ref="balanceInput" v-model="balanceForm.closing_balance" type="number" step="0.01" min="0" placeholder="0.00" :disabled="!canEdit" @keydown.enter.prevent="saveDailyBalance" />
            </div>
            <input v-model.trim="balanceForm.remarks" type="text" maxlength="250" class="bc-input" placeholder="Note (optional), e.g. after salary transfer" :disabled="!canEdit" />
            <button v-if="canEdit" type="button" class="bc-btn-primary w-100" :disabled="balanceSaving || balanceForm.closing_balance === ''" @click="saveDailyBalance">
              <i class="fas" :class="balanceSaving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
              {{ balanceSaving ? "Saving..." : todayEntry ? "Update today's balance" : "Save balance" }}
            </button>
            <small v-if="todayEntry" class="bc-saved-note"><i class="fas fa-check-circle"></i> Entered today{{ todayEntry.entered_by_name ? ` by ${todayEntry.entered_by_name}` : "" }}</small>
          </div>

          <div class="bc-card bc-balance-history">
            <h5>Last 30 days</h5>
            <div v-if="!balanceHistory.length" class="bc-empty small">No balances entered yet.</div>
            <div v-else class="bc-spark" aria-hidden="true">
              <span v-for="entry in sparkPoints" :key="entry.id" :style="{ height: `${entry.height}%` }" :title="`${formatDate(entry.entry_date)}: ${money(entry.closing_balance)}`"></span>
            </div>
            <div v-for="entry in balanceHistory" :key="entry.id" class="bc-hist-row">
              <div>
                <strong>{{ dayLabel(entry.entry_date) }}</strong>
                <small>{{ formatDate(entry.entry_date) }}<template v-if="entry.remarks"> &middot; {{ entry.remarks }}</template></small>
              </div>
              <div class="bc-hist-figs">
                <strong>{{ money(entry.closing_balance) }}</strong>
                <small v-if="entry.change !== null" :class="entry.change >= 0 ? 'in' : 'out'">{{ entry.change >= 0 ? "+" : "−" }}{{ money(Math.abs(entry.change)) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add cheque -->
    <div v-if="showChequeModal" class="bc-modal-backdrop" @click.self="closeChequeModal">
      <div class="bc-modal" role="dialog" aria-modal="true" aria-labelledby="bc-modal-title">
        <div class="bc-modal-head">
          <h5 id="bc-modal-title">Add cheque</h5>
          <button type="button" class="bc-close" aria-label="Close" @click="closeChequeModal"><i class="fas fa-times"></i></button>
        </div>
        <div class="bc-type-cards">
          <button type="button" class="bc-type out" :class="{ on: chequeForm.cheque_type === 'ISSUED' }" @click="chequeForm.cheque_type = 'ISSUED'">
            <i class="fas fa-arrow-up"></i>
            <span><strong>I gave a cheque</strong><small>Issued — money will go out</small></span>
          </button>
          <button type="button" class="bc-type in" :class="{ on: chequeForm.cheque_type === 'RECEIVED' }" @click="chequeForm.cheque_type = 'RECEIVED'">
            <i class="fas fa-arrow-down"></i>
            <span><strong>I received a cheque</strong><small>Received — money will come in</small></span>
          </button>
        </div>

        <label class="bc-label">Amount</label>
        <div class="bc-amount">
          <span>₹</span>
          <input ref="chequeAmount" v-model="chequeForm.amount" type="number" step="0.01" min="0.01" placeholder="0.00" />
        </div>

        <div class="bc-form-grid">
          <label>
            <span class="bc-label">{{ chequeForm.cheque_type === "ISSUED" ? "Paid to" : "Received from" }}</span>
            <input v-model.trim="chequeForm.party_name" list="bc-parties" type="text" class="bc-input" placeholder="Party name" />
            <datalist id="bc-parties"><option v-for="party in parties" :key="party" :value="party" /></datalist>
          </label>
          <label>
            <span class="bc-label">Cheque no.</span>
            <input v-model.trim="chequeForm.cheque_number" type="text" class="bc-input" placeholder="e.g. 004512" />
          </label>
          <label>
            <span class="bc-label">Cheque date</span>
            <input v-model="chequeForm.cheque_date" type="date" class="bc-input" />
          </label>
          <label>
            <span class="bc-label">Expected to clear</span>
            <input v-model="chequeForm.expected_clearance_date" type="date" class="bc-input" />
            <div class="bc-quick">
              <button v-for="days in [0, 2, 3, 7]" :key="days" type="button" :class="{ on: chequeForm.expected_clearance_date === addDays(chequeForm.cheque_date, days) }" @click="chequeForm.expected_clearance_date = addDays(chequeForm.cheque_date, days)">
                {{ days === 0 ? "Same day" : `+${days} days` }}
              </button>
            </div>
          </label>
          <label class="bc-span">
            <span class="bc-label">Note <em>optional</em></span>
            <input v-model.trim="chequeForm.remarks" type="text" maxlength="250" class="bc-input" placeholder="e.g. Advance for May stock" />
          </label>
        </div>
        <div v-if="chequeError" class="bc-error">{{ chequeError }}</div>
        <div class="bc-modal-actions">
          <button type="button" class="bc-btn-light" @click="closeChequeModal">Cancel</button>
          <button type="button" class="bc-btn-light" :disabled="chequeSaving" @click="submitCheque({ another: true })">Save &amp; add another</button>
          <button type="button" class="bc-btn-primary" :disabled="chequeSaving" @click="submitCheque()">
            <i class="fas" :class="chequeSaving ? 'fa-spinner fa-spin' : 'fa-check'"></i> Save cheque
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { useStore } from "vuex";
import { computed, h } from "vue";

function iso(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_LABELS = { PENDING: "Pending", CLEARED: "Cleared", BOUNCED: "Bounced", CANCELLED: "Cancelled" };

// One cheque row, shared by the timeline and the register.
const ChequeLine = {
  name: "ChequeLine",
  props: { cheque: Object, canEdit: Boolean, showDates: Boolean, formatDate: Function, money: Function },
  emits: ["status"],
  setup(props, { emit }) {
    return () => {
      const c = props.cheque;
      const issued = c.cheque_type === "ISSUED";
      const today = iso(new Date());
      const overdue = c.status === "PENDING" && c.expected_clearance_date < today;
      const actions = c.status === "PENDING" && props.canEdit
        ? h("div", { class: "cl-actions" }, [
            h("button", { type: "button", class: "cl-btn ok", onClick: () => emit("status", c, "CLEARED") }, [h("i", { class: "fas fa-check" }), " Cleared"]),
            h("button", { type: "button", class: "cl-btn bad", onClick: () => emit("status", c, "BOUNCED") }, [h("i", { class: "fas fa-times" }), " Bounced"]),
            h("button", { type: "button", class: "cl-btn muted", title: "Cancel cheque", "aria-label": "Cancel cheque", onClick: () => emit("status", c, "CANCELLED") }, [h("i", { class: "fas fa-ban" })]),
          ])
        : h("span", { class: `cl-status st-${c.status.toLowerCase()}` }, STATUS_LABELS[c.status] || c.status);
      return h("div", { class: ["cl-row", { overdue }] }, [
        h("span", { class: ["cl-icon", issued ? "out" : "in"] }, [h("i", { class: issued ? "fas fa-arrow-up" : "fas fa-arrow-down" })]),
        h("div", { class: "cl-main" }, [
          h("strong", c.party_name),
          h("small", [
            `Cheque ${c.cheque_number}`,
            props.showDates ? ` · dated ${props.formatDate(c.cheque_date)} · clears ${props.formatDate(c.expected_clearance_date)}` : "",
            overdue && !props.showDates ? ` · was due ${props.formatDate(c.expected_clearance_date)}` : "",
            c.remarks ? ` · ${c.remarks}` : "",
          ]),
        ]),
        h("strong", { class: ["cl-amount", issued ? "out" : "in"] }, `${issued ? "−" : "+"}${props.money(c.amount)}`),
        actions,
      ]);
    };
  },
};

export default {
  name: "bank-cheques",
  components: { ChequeLine },
  setup() {
    const store = useStore();
    const canEdit = computed(() => store.getters.hasPermission("finance.bank.edit"));
    return { canEdit };
  },
  data() {
    return {
      activeTab: "upcoming",
      loading: false,
      cheques: [],
      lastKnownBalance: 0,
      latestBalanceDate: null,
      balanceEntries: [],
      balanceForm: { closing_balance: "", remarks: "" },
      balanceSaving: false,
      filters: { status: "PENDING", type: "", search: "" },
      showChequeModal: false,
      chequeSaving: false,
      chequeError: "",
      chequeForm: this.emptyChequeForm(),
    };
  },
  computed: {
    today() {
      return iso(new Date());
    },
    pendingCheques() {
      return this.cheques.filter((c) => c.status === "PENDING");
    },
    pending() {
      const sum = (type) => {
        const rows = this.pendingCheques.filter((c) => c.cheque_type === type);
        return { count: rows.length, amount: rows.reduce((s, c) => s + Number(c.amount || 0), 0) };
      };
      return { issued: sum("ISSUED"), received: sum("RECEIVED") };
    },
    overdue() {
      return this.pendingCheques
        .filter((c) => c.expected_clearance_date < this.today)
        .sort((a, b) => a.expected_clearance_date.localeCompare(b.expected_clearance_date));
    },
    projectedEnd() {
      return this.lastKnownBalance + this.pending.received.amount - this.pending.issued.amount;
    },
    timeline() {
      // Overdue cheques are assumed to clear first, so they count towards today's starting balance.
      let running = this.lastKnownBalance + this.overdue.reduce((s, c) => s + (c.cheque_type === "ISSUED" ? -1 : 1) * Number(c.amount || 0), 0);
      const days = new Map();
      this.pendingCheques
        .filter((c) => c.expected_clearance_date >= this.today)
        .sort((a, b) => a.expected_clearance_date.localeCompare(b.expected_clearance_date) || (a.cheque_type === "RECEIVED" ? -1 : 1))
        .forEach((c) => {
          const day = days.get(c.expected_clearance_date) || { date: c.expected_clearance_date, cheques: [], outgoing: 0, incoming: 0 };
          day.cheques.push(c);
          if (c.cheque_type === "ISSUED") day.outgoing += Number(c.amount || 0);
          else day.incoming += Number(c.amount || 0);
          days.set(c.expected_clearance_date, day);
        });
      return [...days.values()].map((day) => {
        running += day.incoming - day.outgoing;
        return { ...day, balanceAfter: running };
      });
    },
    shortfall() {
      if (!this.latestBalanceDate) return null;
      const first = this.timeline.find((day) => day.balanceAfter < 0);
      return first ? { date: first.date, balance: first.balanceAfter } : null;
    },
    balanceIsStale() {
      if (!this.latestBalanceDate) return true;
      return (new Date(this.today) - new Date(this.latestBalanceDate)) / 86400000 > 3;
    },
    tabs() {
      return [
        { key: "upcoming", label: "Upcoming", icon: "fas fa-calendar-alt", count: this.pendingCheques.length },
        { key: "register", label: "All cheques", icon: "fas fa-money-check", count: 0 },
        { key: "balance", label: "Bank balance", icon: "fas fa-university", count: 0 },
      ];
    },
    statusTabs() {
      const count = (status) => this.cheques.filter((c) => (!status || c.status === status) && (!this.filters.type || c.cheque_type === this.filters.type)).length;
      return [
        { value: "PENDING", label: "Pending", count: count("PENDING") },
        { value: "CLEARED", label: "Cleared", count: count("CLEARED") },
        { value: "BOUNCED", label: "Bounced", count: count("BOUNCED") },
        { value: "CANCELLED", label: "Cancelled", count: count("CANCELLED") },
        { value: "", label: "All", count: count("") },
      ];
    },
    registerRows() {
      const term = this.filters.search.toLowerCase();
      return this.cheques
        .filter((c) => !this.filters.status || c.status === this.filters.status)
        .filter((c) => !this.filters.type || c.cheque_type === this.filters.type)
        .filter((c) => !term || c.party_name.toLowerCase().includes(term) || String(c.cheque_number).toLowerCase().includes(term))
        .sort((a, b) => b.expected_clearance_date.localeCompare(a.expected_clearance_date));
    },
    parties() {
      return [...new Set(this.cheques.map((c) => c.party_name).filter(Boolean))].slice(0, 50);
    },
    todayEntry() {
      return this.balanceEntries.find((entry) => entry.entry_date === this.today) || null;
    },
    balanceHistory() {
      const sorted = [...this.balanceEntries].sort((a, b) => b.entry_date.localeCompare(a.entry_date));
      return sorted.map((entry, index) => {
        const previous = sorted[index + 1];
        return { ...entry, change: previous ? Number(entry.closing_balance) - Number(previous.closing_balance) : null };
      });
    },
    sparkPoints() {
      const points = [...this.balanceEntries].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
      const max = Math.max(1, ...points.map((p) => Number(p.closing_balance) || 0));
      return points.map((p) => ({ ...p, height: Math.max(4, Math.round(((Number(p.closing_balance) || 0) / max) * 100)) }));
    },
  },
  mounted() {
    if (["upcoming", "register", "balance"].includes(this.$route.query.tab)) this.activeTab = this.$route.query.tab;
    this.loadAll();
  },
  methods: {
    emptyChequeForm() {
      const today = iso(new Date());
      return { cheque_type: "ISSUED", party_name: "", cheque_number: "", amount: "", cheque_date: today, expected_clearance_date: today, remarks: "" };
    },
    addDays(value, days) {
      const date = new Date(value || new Date());
      date.setDate(date.getDate() + days);
      return iso(date);
    },
    money(value) {
      const number = Number(value || 0);
      return `${number < 0 ? "−" : ""}₹${Math.abs(number).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    dayLabel(value) {
      const diff = Math.round((new Date(value) - new Date(this.today)) / 86400000);
      if (diff === 0) return "Today";
      if (diff === 1) return "Tomorrow";
      if (diff === -1) return "Yesterday";
      if (diff > 1 && diff < 7) return new Date(value).toLocaleDateString("en-IN", { weekday: "long" });
      return diff > 0 ? `In ${diff} days` : `${-diff} days ago`;
    },
    sumOf(rows, type) {
      return rows.filter((c) => c.cheque_type === type).reduce((s, c) => s + Number(c.amount || 0), 0);
    },
    setTab(tab) {
      this.activeTab = tab;
      this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
      if (tab === "balance") this.$nextTick(() => this.$refs.balanceInput?.focus());
    },
    showRegister(status, type) {
      this.filters = { status, type, search: "" };
      this.setTab("register");
    },
    openBalance() {
      this.setTab("balance");
    },
    async loadAll() {
      this.loading = true;
      try {
        const [cheques, latest, balances] = await Promise.all([
          axios.get("/v1/cheques"),
          axios.get("/v1/bank/daily-balance/latest").catch(() => ({ data: null })),
          axios.get("/v1/bank/daily-balance", { params: { from: this.addDays(this.today, -29), to: this.today } }).catch(() => ({ data: [] })),
        ]);
        this.cheques = (cheques.data || []).map((c) => ({ ...c, amount: Number(c.amount || 0) }));
        this.lastKnownBalance = Number(latest.data?.closing_balance || 0);
        this.latestBalanceDate = latest.data?.entry_date || null;
        this.balanceEntries = balances.data || [];
        const todayEntry = this.balanceEntries.find((e) => e.entry_date === this.today);
        this.balanceForm = { closing_balance: todayEntry ? todayEntry.closing_balance : "", remarks: todayEntry?.remarks || "" };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not load", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    openChequeModal() {
      this.chequeForm = this.emptyChequeForm();
      this.chequeError = "";
      this.showChequeModal = true;
      this.$nextTick(() => this.$refs.chequeAmount?.focus());
    },
    closeChequeModal() {
      this.showChequeModal = false;
    },
    async submitCheque({ another = false } = {}) {
      const f = this.chequeForm;
      if (!(Number(f.amount) > 0)) return (this.chequeError = "Enter the cheque amount.");
      if (!f.party_name) return (this.chequeError = f.cheque_type === "ISSUED" ? "Who is the cheque paid to?" : "Who gave you the cheque?");
      if (!f.cheque_number) return (this.chequeError = "Enter the cheque number.");
      if (!f.cheque_date || !f.expected_clearance_date) return (this.chequeError = "Choose the cheque and clearance dates.");
      if (f.expected_clearance_date < f.cheque_date) return (this.chequeError = "Clearance date can't be before the cheque date.");
      this.chequeError = "";
      this.chequeSaving = true;
      try {
        await axios.post("/v1/cheques", { ...f, amount: Number(f.amount), remarks: f.remarks || null });
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Cheque ${f.cheque_number} saved`, showConfirmButton: false, timer: 1500 });
        await this.loadAll();
        if (another) {
          this.chequeForm = { ...this.emptyChequeForm(), cheque_type: f.cheque_type, cheque_date: f.cheque_date, expected_clearance_date: f.expected_clearance_date };
          this.$nextTick(() => this.$refs.chequeAmount?.focus());
        } else {
          this.showChequeModal = false;
        }
      } catch (error) {
        this.chequeError = error.response?.data?.detail || "Failed to save cheque.";
      } finally {
        this.chequeSaving = false;
      }
    },
    async updateChequeStatus(cheque, status) {
      const words = { CLEARED: "cleared", BOUNCED: "bounced", CANCELLED: "cancelled" };
      const result = await Swal.fire({
        icon: status === "CLEARED" ? "question" : "warning",
        title: `Mark cheque ${cheque.cheque_number} as ${words[status]}?`,
        html: `${String(cheque.party_name).replace(/[<>&]/g, "")} &middot; <strong>${this.money(cheque.amount)}</strong>${status === "CLEARED" ? "<br><small>Remember to update the bank balance.</small>" : ""}`,
        showCancelButton: true,
        confirmButtonText: `Yes, ${words[status]}`,
        confirmButtonColor: status === "CLEARED" ? "#16a34a" : status === "BOUNCED" ? "#dc2626" : "#6b7280",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.patch(`/v1/cheques/${cheque.chequeid}/status`, { status });
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Marked ${words[status]}`, showConfirmButton: false, timer: 1400 });
        await this.loadAll();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not update", text: error.response?.data?.detail || error.message });
      }
    },
    async saveDailyBalance() {
      if (this.balanceForm.closing_balance === "" || this.balanceSaving) return;
      this.balanceSaving = true;
      try {
        await axios.post("/v1/bank/daily-balance", {
          entry_date: this.today,
          closing_balance: Number(this.balanceForm.closing_balance),
          remarks: this.balanceForm.remarks || null,
        });
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Bank balance saved", showConfirmButton: false, timer: 1500 });
        await this.loadAll();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not save", text: error.response?.data?.detail || error.message });
      } finally {
        this.balanceSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.bc-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 16px; }
.bc-head h3 { margin: 0; font-size: 20px; font-weight: 700; color: #111827; }
.bc-head p { margin: 2px 0 0; font-size: 13px; color: #6b7280; }
.bc-head-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.bc-btn-primary, .bc-btn-light { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 40px; padding: 0 16px; border-radius: 10px; font-size: 14px; font-weight: 600; white-space: nowrap; }
.bc-btn-primary { background: #FF9F43; color: #fff; border: 0; }
.bc-btn-primary:disabled { opacity: 0.55; }
.bc-btn-light { background: #fff; color: #374151; border: 1px solid #e5e7eb; }
.bc-btn-light:hover { border-color: #FF9F43; color: #c2410c; }
.out { color: #dc2626; }
.in { color: #15803d; }

.bc-kpis { display: grid; grid-template-columns: 1.2fr 1fr 1fr 1fr 1.2fr; gap: 12px; margin-bottom: 14px; }
.bc-kpi { display: flex; flex-direction: column; gap: 2px; text-align: left; padding: 14px 16px; border-radius: 14px; background: #fff; border: 1px solid #e9ecf2; min-width: 0; }
button.bc-kpi:hover { border-color: #fdba74; }
.bc-kpi span { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: #6b7280; display: flex; align-items: center; gap: 6px; }
.bc-kpi strong { font-size: 21px; color: #111827; font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bc-kpi small { font-size: 12px; color: #9ca3af; }
.bc-kpi small.stale { color: #b45309; }
.bc-kpi-balance { background: linear-gradient(135deg, #eff6ff, #dbeafe); border-color: #bfdbfe; }
.bc-kpi-balance strong { color: #1e3a8a; }
.bc-kpi.alert { border-color: #fecaca; background: #fef2f2; }
.bc-kpi.alert strong { color: #dc2626; }
.bc-kpi-projected strong { color: #15803d; }
.bc-kpi-projected.negative { background: #fef2f2; border-color: #fecaca; }
.bc-kpi-projected.negative strong { color: #dc2626; }

.bc-warning { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; margin-bottom: 14px; border-radius: 12px; background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412; font-size: 13.5px; }
.bc-warning i { margin-top: 3px; }

.bc-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e7eb; margin-bottom: 14px; overflow-x: auto; }
.bc-tabs button { border: 0; background: transparent; padding: 10px 14px; font-size: 14px; font-weight: 600; color: #6b7280; border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; display: inline-flex; align-items: center; gap: 8px; }
.bc-tabs button span { padding: 0 8px; border-radius: 999px; background: #f3f4f6; font-size: 11.5px; }
.bc-tabs button.on { color: #c2410c; border-bottom-color: #FF9F43; }
.bc-tabs button.on span { background: #fff4e8; }

.bc-card { background: #fff; border: 1px solid #e9ecf2; border-radius: 14px; padding: 14px 16px; }
.bc-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 36px 16px; color: #6b7280; font-size: 14px; text-align: center; }
.bc-empty.small { padding: 16px; }
.bc-empty strong { color: #111827; font-size: 16px; }
.bc-empty-icon { width: 54px; height: 54px; border-radius: 16px; background: #eff6ff; color: #3b82f6; display: flex; align-items: center; justify-content: center; font-size: 22px; }

.bc-timeline-card { padding: 8px 16px 14px; }
.bc-day { position: relative; padding-left: 18px; margin-top: 10px; border-left: 2px solid #e5e7eb; }
.bc-day::before { content: ""; position: absolute; left: -7px; top: 6px; width: 12px; height: 12px; border-radius: 50%; background: #fff; border: 3px solid #FF9F43; }
.bc-day.overdue { border-left-color: #fecaca; }
.bc-day.overdue::before { border-color: #dc2626; }
.bc-day-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
.bc-day-head strong { font-size: 14.5px; color: #111827; display: block; }
.bc-day.overdue .bc-day-head strong { color: #dc2626; }
.bc-day-head small { font-size: 12px; color: #9ca3af; }
.bc-day-figures { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
.bc-proj { padding: 3px 10px; border-radius: 999px; background: #f0fdf4; color: #166534; font-weight: 500; }
.bc-proj.negative { background: #fef2f2; color: #b91c1c; }

.bc-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.bc-seg { display: inline-flex; padding: 3px; border-radius: 10px; background: #f3f4f6; flex-wrap: wrap; }
.bc-seg button { border: 0; background: transparent; padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 600; color: #6b7280; display: inline-flex; align-items: center; gap: 5px; }
.bc-seg button span { padding: 0 7px; border-radius: 999px; background: #e5e7eb; font-size: 11px; }
.bc-seg button.on { background: #fff; color: #111827; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }
.bc-search { position: relative; flex: 1 1 200px; max-width: 300px; }
.bc-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.bc-search input { width: 100%; height: 36px; padding-left: 34px; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 13px; }
.bc-register { border: 1px solid #f1f2f4; border-radius: 12px; overflow: hidden; }
.bc-register-total { margin-top: 10px; text-align: right; font-size: 13px; color: #6b7280; }

.bc-balance-layout { display: grid; grid-template-columns: 380px minmax(0, 1fr); gap: 16px; align-items: start; }
.bc-balance-entry { padding: 18px; display: flex; flex-direction: column; gap: 10px; }
.bc-balance-entry h5, .bc-balance-history h5 { margin: 0; font-size: 15px; font-weight: 700; color: #111827; }
.bc-balance-entry p { margin: 0; font-size: 12.5px; color: #6b7280; }
.bc-amount { display: flex; align-items: center; border: 2px solid #e5e7eb; border-radius: 14px; padding: 0 14px; background: #fafafa; }
.bc-amount:focus-within { border-color: #FF9F43; background: #fff; box-shadow: 0 0 0 4px rgba(255, 159, 67, 0.12); }
.bc-amount span { font-size: 22px; color: #9ca3af; font-weight: 600; }
.bc-amount input { flex: 1; min-width: 0; border: 0; background: transparent; height: 56px; padding: 0 8px; font-size: 26px; font-weight: 700; color: #111827; font-variant-numeric: tabular-nums; }
.bc-amount input:focus { outline: none; }
.bc-input { width: 100%; height: 40px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 0 12px; font-size: 14px; background: #fff; }
.bc-input:focus { outline: none; border-color: #FF9F43; }
.bc-saved-note { font-size: 12.5px; color: #15803d; }
.bc-balance-history { padding: 16px 18px; }
.bc-spark { display: flex; align-items: flex-end; gap: 3px; height: 70px; margin: 12px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #f1f2f4; }
.bc-spark span { flex: 1; max-width: 22px; border-radius: 4px 4px 0 0; background: #93c5fd; }
.bc-spark span:last-child { background: #2563eb; }
.bc-hist-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 9px 0; border-bottom: 1px solid #f3f4f6; }
.bc-hist-row:last-child { border-bottom: 0; }
.bc-hist-row strong { display: block; font-size: 13.5px; color: #111827; }
.bc-hist-row small { font-size: 12px; color: #9ca3af; }
.bc-hist-figs { text-align: right; font-variant-numeric: tabular-nums; }
.bc-hist-figs small.in { color: #15803d; }
.bc-hist-figs small.out { color: #dc2626; }

/* cheque line (child component) */
.bc-card :deep(.cl-row) { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 10px; }
.bc-day :deep(.cl-row) { background: #fafbfc; margin-bottom: 6px; }
.bc-register :deep(.cl-row) { border-radius: 0; border-bottom: 1px solid #f3f4f6; }
.bc-register :deep(.cl-row:last-child) { border-bottom: 0; }
.bc-card :deep(.cl-row:hover) { background: #fffbf7; }
.bc-card :deep(.cl-row.overdue) { background: #fef2f2; }
.bc-card :deep(.cl-icon) { width: 32px; height: 32px; flex: 0 0 32px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 13px; }
.bc-card :deep(.cl-icon.out) { background: #fee2e2; color: #dc2626; }
.bc-card :deep(.cl-icon.in) { background: #dcfce7; color: #15803d; }
.bc-card :deep(.cl-main) { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.bc-card :deep(.cl-main strong) { font-size: 14px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bc-card :deep(.cl-main small) { font-size: 12px; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bc-card :deep(.cl-amount) { font-size: 15px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.bc-card :deep(.cl-actions) { display: flex; gap: 4px; }
.bc-card :deep(.cl-btn) { border: 0; border-radius: 8px; padding: 5px 10px; font-size: 12.5px; font-weight: 600; white-space: nowrap; }
.bc-card :deep(.cl-btn.ok) { background: #dcfce7; color: #166534; }
.bc-card :deep(.cl-btn.bad) { background: #fee2e2; color: #b91c1c; }
.bc-card :deep(.cl-btn.muted) { background: #f3f4f6; color: #6b7280; }
.bc-card :deep(.cl-status) { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.bc-card :deep(.st-pending) { background: #fef3c7; color: #92400e; }
.bc-card :deep(.st-cleared) { background: #dcfce7; color: #166534; }
.bc-card :deep(.st-bounced) { background: #fee2e2; color: #991b1b; }
.bc-card :deep(.st-cancelled) { background: #f3f4f6; color: #4b5563; }

/* modal */
.bc-modal-backdrop { position: fixed; inset: 0; z-index: 1200; background: rgba(15, 23, 42, 0.45); display: flex; align-items: center; justify-content: center; padding: 16px; }
.bc-modal { width: min(100%, 600px); max-height: calc(100vh - 32px); overflow-y: auto; background: #fff; border-radius: 16px; padding: 20px; box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25); }
.bc-modal-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.bc-modal-head h5 { margin: 0; font-size: 17px; font-weight: 700; }
.bc-close { width: 34px; height: 34px; border: 0; border-radius: 8px; background: #f3f4f6; color: #4b5563; }
.bc-type-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 6px; }
.bc-type { display: flex; align-items: center; gap: 10px; text-align: left; padding: 12px; border: 2px solid #e5e7eb; border-radius: 12px; background: #fff; }
.bc-type > i { width: 34px; height: 34px; flex: 0 0 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.bc-type.out > i { background: #fee2e2; color: #dc2626; }
.bc-type.in > i { background: #dcfce7; color: #15803d; }
.bc-type span { display: flex; flex-direction: column; }
.bc-type strong { font-size: 14px; color: #111827; }
.bc-type small { font-size: 12px; color: #6b7280; }
.bc-type.out.on { border-color: #f87171; background: #fef2f2; }
.bc-type.in.on { border-color: #4ade80; background: #f0fdf4; }
.bc-label { display: block; font-size: 12.5px; font-weight: 600; color: #374151; margin: 12px 0 6px; }
.bc-label em { font-style: normal; font-weight: 400; color: #9ca3af; }
.bc-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12px; }
.bc-form-grid label { margin: 0; }
.bc-span { grid-column: 1 / -1; }
.bc-quick { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
.bc-quick button { border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 2px 10px; font-size: 12px; color: #6b7280; }
.bc-quick button.on { border-color: #111827; background: #111827; color: #fff; }
.bc-error { margin-top: 10px; padding: 8px 12px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 13px; }
.bc-modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; flex-wrap: wrap; }

@media (max-width: 1199px) {
  .bc-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .bc-balance-layout { grid-template-columns: 1fr; }
}
@media (max-width: 767px) {
  .bc-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .bc-kpi-projected { grid-column: 1 / -1; }
  .bc-type-cards, .bc-form-grid { grid-template-columns: 1fr; }
  .bc-card :deep(.cl-row) { flex-wrap: wrap; }
  .bc-card :deep(.cl-actions) { width: 100%; justify-content: flex-end; }
}
</style>
